import { useQuery, useMutation, gql } from '@apollo/client';

// ==================== TYPES ====================

export type DayOfWeek = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';

export const dayOfWeekLabels: Record<DayOfWeek, string> = {
  MONDAY: 'Lunes',
  TUESDAY: 'Martes',
  WEDNESDAY: 'Miércoles',
  THURSDAY: 'Jueves',
  FRIDAY: 'Viernes',
  SATURDAY: 'Sábado',
  SUNDAY: 'Domingo',
};

export const dayOfWeekShortLabels: Record<DayOfWeek, string> = {
  MONDAY: 'Lun',
  TUESDAY: 'Mar',
  WEDNESDAY: 'Mié',
  THURSDAY: 'Jue',
  FRIDAY: 'Vie',
  SATURDAY: 'Sáb',
  SUNDAY: 'Dom',
};

export const dayOfWeekOrder: DayOfWeek[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

export interface HourlyDistribution {
  id: string;
  dayOfWeek: DayOfWeek;
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
  percentage: number;
  isActive: boolean;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DayDistributionSummary {
  dayOfWeek: DayOfWeek;
  label: string;
  totalDistributions: number;
  activeDistributions: number;
  totalPercentage: number;
  isComplete: boolean;
}

export interface DayAlert {
  dayOfWeek: DayOfWeek;
  label: string;
  unassigned: number;
}

export interface DistributionAlert {
  hasAlert: boolean;
  unassignedPercentage: number;
  totalAssigned: number;
  message?: string;
  alertsByDay?: DayAlert[];
}

export interface DistributeUniformlyResult {
  success: boolean;
  distributions: HourlyDistribution[];
  totalCreated: number;
}

export interface CopyDistributionsResult {
  success: boolean;
  copiedTo: DayOfWeek[];
  totalCopied: number;
}

// ==================== FRAGMENT ====================

const DISTRIBUTION_FRAGMENT = gql`
  fragment DistributionFields on HourlyDistribution {
    id
    dayOfWeek
    startHour
    startMinute
    endHour
    endMinute
    percentage
    isActive
    description
    createdAt
    updatedAt
  }
`;

// ==================== QUERIES ====================

const GET_ALL_HOURLY_DISTRIBUTIONS = gql`
  ${DISTRIBUTION_FRAGMENT}
  query GetAllHourlyDistributions {
    getAllHourlyDistributions {
      ...DistributionFields
    }
  }
`;

const GET_HOURLY_DISTRIBUTIONS_BY_DAY = gql`
  ${DISTRIBUTION_FRAGMENT}
  query GetHourlyDistributionsByDay($dayOfWeek: DayOfWeek!) {
    getHourlyDistributionsByDay(dayOfWeek: $dayOfWeek) {
      ...DistributionFields
    }
  }
`;

const GET_ACTIVE_HOURLY_DISTRIBUTIONS = gql`
  ${DISTRIBUTION_FRAGMENT}
  query GetActiveHourlyDistributions {
    getActiveHourlyDistributions {
      ...DistributionFields
    }
  }
`;

const GET_ACTIVE_HOURLY_DISTRIBUTIONS_BY_DAY = gql`
  ${DISTRIBUTION_FRAGMENT}
  query GetActiveHourlyDistributionsByDay($dayOfWeek: DayOfWeek!) {
    getActiveHourlyDistributionsByDay(dayOfWeek: $dayOfWeek) {
      ...DistributionFields
    }
  }
`;

const GET_DISTRIBUTIONS_SUMMARY_BY_DAY = gql`
  query GetDistributionsSummaryByDay {
    getDistributionsSummaryByDay {
      dayOfWeek
      label
      totalDistributions
      activeDistributions
      totalPercentage
      isComplete
    }
  }
`;

const GET_DISTRIBUTION_ALERT = gql`
  query GetDistributionAlert {
    getDistributionAlert {
      hasAlert
      unassignedPercentage
      totalAssigned
      message
      alertsByDay {
        dayOfWeek
        label
        unassigned
      }
    }
  }
`;

const GET_DISTRIBUTION_ALERT_BY_DAY = gql`
  query GetDistributionAlertByDay($dayOfWeek: DayOfWeek!) {
    getDistributionAlertByDay(dayOfWeek: $dayOfWeek) {
      hasAlert
      unassignedPercentage
      totalAssigned
      message
    }
  }
`;

const GET_TOTAL_ASSIGNED_PERCENTAGE_BY_DAY = gql`
  query GetTotalAssignedPercentageByDay($dayOfWeek: DayOfWeek!) {
    getTotalAssignedPercentageByDay(dayOfWeek: $dayOfWeek)
  }
`;

const GET_UNASSIGNED_PERCENTAGE_BY_DAY = gql`
  query GetUnassignedPercentageByDay($dayOfWeek: DayOfWeek!) {
    getUnassignedPercentageByDay(dayOfWeek: $dayOfWeek)
  }
`;

// ==================== MUTATIONS ====================

const CREATE_HOURLY_DISTRIBUTION = gql`
  ${DISTRIBUTION_FRAGMENT}
  mutation CreateHourlyDistribution($input: CreateHourlyDistributionInput!) {
    createHourlyDistribution(input: $input) {
      ...DistributionFields
    }
  }
`;

const UPDATE_HOURLY_DISTRIBUTION = gql`
  ${DISTRIBUTION_FRAGMENT}
  mutation UpdateHourlyDistribution($input: UpdateHourlyDistributionInput!) {
    updateHourlyDistribution(input: $input) {
      ...DistributionFields
    }
  }
`;

const DELETE_HOURLY_DISTRIBUTION = gql`
  mutation DeleteHourlyDistribution($id: ID!) {
    deleteHourlyDistribution(id: $id)
  }
`;

const DELETE_HOURLY_DISTRIBUTIONS_BY_DAY = gql`
  mutation DeleteHourlyDistributionsByDay($dayOfWeek: DayOfWeek!) {
    deleteHourlyDistributionsByDay(dayOfWeek: $dayOfWeek)
  }
`;

const DISTRIBUTE_UNIFORMLY = gql`
  ${DISTRIBUTION_FRAGMENT}
  mutation DistributeUniformly($input: DistributeUniformlyInput!) {
    distributeUniformly(input: $input) {
      success
      distributions {
        ...DistributionFields
      }
      totalCreated
    }
  }
`;

const COPY_DISTRIBUTIONS = gql`
  mutation CopyDistributions($input: CopyDistributionsInput!) {
    copyDistributions(input: $input) {
      success
      copiedTo
      totalCopied
    }
  }
`;

const RESET_ALL_HOURLY_DISTRIBUTIONS = gql`
  mutation ResetAllHourlyDistributions {
    resetAllHourlyDistributions
  }
`;

// ==================== HOOKS ====================

/**
 * Obtener todas las distribuciones (todos los días)
 */
export const useAllHourlyDistributions = () => {
  const { data, loading, error, refetch } = useQuery(GET_ALL_HOURLY_DISTRIBUTIONS, {
    fetchPolicy: 'network-only',
  });

  return {
    distributions: (data?.getAllHourlyDistributions || []) as HourlyDistribution[],
    loading,
    error,
    refetch,
  };
};

/**
 * Obtener distribuciones para un día específico
 */
export const useHourlyDistributionsByDay = (dayOfWeek: DayOfWeek) => {
  const { data, loading, error, refetch } = useQuery(GET_HOURLY_DISTRIBUTIONS_BY_DAY, {
    variables: { dayOfWeek },
    fetchPolicy: 'network-only',
  });

  return {
    distributions: (data?.getHourlyDistributionsByDay || []) as HourlyDistribution[],
    loading,
    error,
    refetch,
  };
};

/**
 * Obtener distribuciones activas del día actual
 */
export const useActiveHourlyDistributions = () => {
  const { data, loading, error, refetch } = useQuery(GET_ACTIVE_HOURLY_DISTRIBUTIONS, {
    fetchPolicy: 'network-only',
  });

  return {
    distributions: (data?.getActiveHourlyDistributions || []) as HourlyDistribution[],
    loading,
    error,
    refetch,
  };
};

/**
 * Obtener distribuciones activas para un día específico
 */
export const useActiveHourlyDistributionsByDay = (dayOfWeek: DayOfWeek) => {
  const { data, loading, error, refetch } = useQuery(GET_ACTIVE_HOURLY_DISTRIBUTIONS_BY_DAY, {
    variables: { dayOfWeek },
    fetchPolicy: 'network-only',
  });

  return {
    distributions: (data?.getActiveHourlyDistributionsByDay || []) as HourlyDistribution[],
    loading,
    error,
    refetch,
  };
};

/**
 * Obtener resumen de distribuciones por día de la semana
 */
export const useDistributionsSummaryByDay = () => {
  const { data, loading, error, refetch } = useQuery(GET_DISTRIBUTIONS_SUMMARY_BY_DAY, {
    fetchPolicy: 'network-only',
  });

  return {
    summary: (data?.getDistributionsSummaryByDay || []) as DayDistributionSummary[],
    loading,
    error,
    refetch,
  };
};

/**
 * Obtener alertas de distribución (general)
 */
export const useDistributionAlert = () => {
  const { data, loading, error, refetch } = useQuery(GET_DISTRIBUTION_ALERT, {
    fetchPolicy: 'network-only',
  });

  return {
    alert: data?.getDistributionAlert as DistributionAlert | null,
    loading,
    error,
    refetch,
  };
};

/**
 * Obtener alertas de distribución para un día específico
 */
export const useDistributionAlertByDay = (dayOfWeek: DayOfWeek) => {
  const { data, loading, error, refetch } = useQuery(GET_DISTRIBUTION_ALERT_BY_DAY, {
    variables: { dayOfWeek },
    fetchPolicy: 'network-only',
  });

  return {
    alert: data?.getDistributionAlertByDay as DistributionAlert | null,
    loading,
    error,
    refetch,
  };
};

/**
 * Obtener porcentaje asignado para un día específico
 */
export const useTotalAssignedPercentageByDay = (dayOfWeek: DayOfWeek) => {
  const { data, loading, error, refetch } = useQuery(GET_TOTAL_ASSIGNED_PERCENTAGE_BY_DAY, {
    variables: { dayOfWeek },
    fetchPolicy: 'network-only',
  });

  return {
    totalAssigned: (data?.getTotalAssignedPercentageByDay || 0) as number,
    loading,
    error,
    refetch,
  };
};

/**
 * Obtener porcentaje sin asignar para un día específico
 */
export const useUnassignedPercentageByDay = (dayOfWeek: DayOfWeek) => {
  const { data, loading, error, refetch } = useQuery(GET_UNASSIGNED_PERCENTAGE_BY_DAY, {
    variables: { dayOfWeek },
    fetchPolicy: 'network-only',
  });

  return {
    unassigned: (data?.getUnassignedPercentageByDay || 0) as number,
    loading,
    error,
    refetch,
  };
};

/**
 * Crear una nueva distribución horaria
 */
export const useCreateHourlyDistribution = () => {
  const [createMutation, { loading }] = useMutation(CREATE_HOURLY_DISTRIBUTION);

  const create = async (input: {
    dayOfWeek: DayOfWeek;
    startHour: number;
    startMinute: number;
    endHour: number;
    endMinute: number;
    percentage: number;
    description?: string;
  }) => {
    const { data } = await createMutation({
      variables: { input },
    });
    return data.createHourlyDistribution as HourlyDistribution;
  };

  return { create, creating: loading };
};

/**
 * Actualizar una distribución existente
 */
export const useUpdateHourlyDistribution = () => {
  const [updateMutation, { loading }] = useMutation(UPDATE_HOURLY_DISTRIBUTION);

  const update = async (input: {
    id: string;
    dayOfWeek?: DayOfWeek;
    startHour?: number;
    startMinute?: number;
    endHour?: number;
    endMinute?: number;
    percentage?: number;
    description?: string;
    isActive?: boolean;
  }) => {
    const { data } = await updateMutation({
      variables: { input },
    });
    return data.updateHourlyDistribution as HourlyDistribution;
  };

  return { update, updating: loading };
};

/**
 * Eliminar una distribución
 */
export const useDeleteHourlyDistribution = () => {
  const [deleteMutation, { loading }] = useMutation(DELETE_HOURLY_DISTRIBUTION);

  const deleteDistribution = async (id: string) => {
    const { data } = await deleteMutation({
      variables: { id },
    });
    return data.deleteHourlyDistribution as boolean;
  };

  return { deleteDistribution, deleting: loading };
};

/**
 * Eliminar todas las distribuciones de un día
 */
export const useDeleteHourlyDistributionsByDay = () => {
  const [deleteMutation, { loading }] = useMutation(DELETE_HOURLY_DISTRIBUTIONS_BY_DAY);

  const deleteByDay = async (dayOfWeek: DayOfWeek) => {
    const { data } = await deleteMutation({
      variables: { dayOfWeek },
    });
    return data.deleteHourlyDistributionsByDay as number;
  };

  return { deleteByDay, deleting: loading };
};

/**
 * Distribuir uniformemente las horas de un día
 */
export const useDistributeUniformly = () => {
  const [distributeMutation, { loading }] = useMutation(DISTRIBUTE_UNIFORMLY);

  const distribute = async (input: {
    dayOfWeek: DayOfWeek;
    businessStartHour: number;
    businessEndHour: number;
    clearExisting?: boolean;
  }) => {
    const { data } = await distributeMutation({
      variables: { input },
    });
    return data.distributeUniformly as DistributeUniformlyResult;
  };

  return { distribute, distributing: loading };
};

/**
 * Copiar distribuciones de un día a otros
 */
export const useCopyDistributions = () => {
  const [copyMutation, { loading }] = useMutation(COPY_DISTRIBUTIONS);

  const copy = async (input: {
    fromDay: DayOfWeek;
    toDays: DayOfWeek[];
    overwrite?: boolean;
  }) => {
    const { data } = await copyMutation({
      variables: { input },
    });
    return data.copyDistributions as CopyDistributionsResult;
  };

  return { copy, copying: loading };
};

/**
 * Resetear todas las distribuciones de todos los días
 */
export const useResetAllHourlyDistributions = () => {
  const [resetMutation, { loading }] = useMutation(RESET_ALL_HOURLY_DISTRIBUTIONS);

  const reset = async () => {
    const { data } = await resetMutation();
    return data.resetAllHourlyDistributions as boolean;
  };

  return { reset, resetting: loading };
};

// ==================== HELPERS ====================

/**
 * Obtener el día de la semana actual
 */
export const getCurrentDayOfWeek = (): DayOfWeek => {
  const days: DayOfWeek[] = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
  return days[new Date().getDay()];
};

/**
 * Obtener el día de la semana para una fecha
 */
export const getDayOfWeekForDate = (date: Date): DayOfWeek => {
  const days: DayOfWeek[] = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
  return days[date.getDay()];
};

/**
 * Formatear hora
 */
export const formatTime = (hour: number, minute: number): string => {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const displayMinute = minute.toString().padStart(2, '0');
  return `${displayHour}:${displayMinute} ${period}`;
};
